/**
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v2
 */
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { X } from 'lucide-react';
import { Chip } from '../../components/chip';
import { useToast } from '../../components/toast';
import { RoleButton } from '../../components/role-gate';
import {
    useUser,
    useUserAuditLog,
    useUserBillingHealth,
    useUserDevices,
    useUserLegalAcceptance
} from '../../../lib/api';
import { apiPost, getHttpErrorMessage } from '../../../lib/http';
import { getAuditSource, getAuditTargetId, groupAuditEntriesByDay } from '../../../lib/audit';
import type { AdminAuditEntry, AdminUser, UserAuditEntry } from '../../../lib/types';

const fmtCurrency = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const planTone = (plan: AdminUser['plan']) =>
    plan === 'studio' || plan === 'team'
        ? 'purple'
        : plan === 'pro'
          ? 'info'
          : plan === 'trial'
            ? 'warning'
            : 'muted';

export function UserDetailPanel({ userId, onClose }: { userId: string; onClose: () => void }) {
    const user = useUser(userId);
    const billing = useUserBillingHealth(userId);
    const legal = useUserLegalAcceptance(userId);
    const audit = useUserAuditLog(userId);
    const devices = useUserDevices(userId);
    const qc = useQueryClient();
    const { toast } = useToast();
    const [inflight, setInflight] = useState<string | null>(null);

    if (!user.data) {
        return (
            <div className="detail-panel-header">
                <div className="empty-state">Loading…</div>
            </div>
        );
    }

    const u = user.data;
    const storagePct = Math.min(100, (u.storage_gb / u.storage_limit_gb) * 100);
    const storageTone = storagePct > 90 ? 'danger' : storagePct > 70 ? 'warning' : undefined;
    const auditRows = (audit.data ?? []).map((entry) => normalizeUserAuditEntry(entry, u));
    const auditGroups = groupAuditEntriesByDay(auditRows);

    const refreshUserQueries = () =>
        Promise.all([
            qc.invalidateQueries({ queryKey: ['admin', 'users', 'detail', userId] }),
            qc.invalidateQueries({ queryKey: ['admin', 'users', 'list'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'users', 'segments'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'users', 'legal', userId] }),
            qc.invalidateQueries({ queryKey: ['admin', 'users', 'audit', userId] })
        ]);

    const runAction = async ({
        key,
        label,
        endpoint,
        body,
        successMessage,
        successKind = 'success'
    }: {
        key: string;
        label: string;
        endpoint: string;
        body?: unknown;
        successMessage: string;
        successKind?: 'success' | 'info';
    }) => {
        if (inflight) return;
        setInflight(key);

        try {
            await apiPost<{ ok: true }>(endpoint, body);
            await refreshUserQueries();
            toast(successMessage, { kind: successKind });
        } catch (error) {
            toast(`${label} failed`, {
                kind: 'danger',
                detail: getHttpErrorMessage(error, `Could not ${label.toLowerCase()}`)
            });
        } finally {
            setInflight(null);
        }
    };

    const handleImpersonate = async () => {
        if (u.is_admin) {
            toast('Admin users cannot be impersonated', {
                kind: 'danger',
                detail: 'Choose a non-admin user instead.'
            });
            return;
        }

        const reason = window
            .prompt(`Reason for impersonating ${u.email}`, 'support view-as')
            ?.trim();
        if (!reason) {
            toast('Impersonation reason required', {
                kind: 'info',
                detail: 'Enter a short reason before starting the session.'
            });
            return;
        }

        await runAction({
            key: 'impersonate',
            label: 'Impersonate',
            endpoint: `/api/admin/users/${u.id}/impersonate`,
            body: { reason },
            successMessage: `Impersonation started for ${u.email}`,
            successKind: 'info'
        });
    };

    const handleMagicLink = async () => {
        await runAction({
            key: 'magic-link',
            label: 'Send magic link',
            endpoint: `/api/admin/users/${u.id}/magic-link`,
            successMessage: `Magic link sent to ${u.email}`
        });
    };

    const handleForceReauth = async () => {
        await runAction({
            key: 'force-reauth',
            label: 'Force re-auth',
            endpoint: `/api/admin/users/${u.id}/force-reauth`,
            successMessage: `Forced re-auth on next session for ${u.email}`,
            successKind: 'info'
        });
    };

    const handleForceTos = async () => {
        await runAction({
            key: 'force-tos',
            label: 'Force ToS re-accept',
            endpoint: `/api/admin/users/${u.id}/force-tos-reaccept`,
            successMessage: `Forced Terms re-accept for ${u.email}`,
            successKind: 'info'
        });
    };

    const handleToggleSuspend = async () => {
        const nextStatus = u.status === 'suspended' ? 'active' : 'suspended';
        const reason =
            nextStatus === 'suspended'
                ? window.prompt(`Suspension reason for ${u.email}`)?.trim()
                : '';

        if (nextStatus === 'suspended' && !reason) {
            toast('Reason required', {
                kind: 'danger',
                detail: 'Suspensions require a reason for audit tracing.'
            });
            return;
        }

        await runAction({
            key: 'toggle-suspend',
            label: nextStatus === 'suspended' ? 'Suspend user' : 'Unsuspend user',
            endpoint:
                nextStatus === 'suspended'
                    ? `/api/admin/users/${u.id}/suspend`
                    : `/api/admin/users/${u.id}/unsuspend`,
            body:
                nextStatus === 'suspended'
                    ? {
                          reason
                      }
                    : undefined,
            successMessage:
                nextStatus === 'suspended'
                    ? `Suspended ${u.email}`
                    : `Restored access for ${u.email}`,
            successKind: nextStatus === 'suspended' ? 'info' : 'success'
        });
    };

    return (
        <>
            <header className="detail-panel-header">
                <div className="avatar">
                    {u.name
                        .split(' ')
                        .map((p) => p[0])
                        .slice(0, 2)
                        .join('')}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 className="name">{u.name}</h2>
                    <p className="email">{u.email}</p>
                    <span className="uid">{u.id}</span>
                </div>
                <button className="close" onClick={onClose} aria-label="Close">
                    <X size={16} />
                </button>
            </header>

            <div className="detail-panel-chips">
                <Chip tone={u.status === 'suspended' ? 'danger' : 'success'} dot>
                    {u.status}
                </Chip>
                <Chip tone={planTone(u.plan)}>{u.plan}</Chip>
                {u.is_admin && <Chip tone="purple">Admin</Chip>}
                <Chip tone="muted">{u.platform}</Chip>
                <Chip tone="muted">v{u.app_version}</Chip>
                <Chip tone={u.email_verified ? 'success' : 'warning'}>
                    {u.email_verified ? 'Email verified' : 'Email unverified'}
                </Chip>
            </div>

            <section className="detail-panel-section">
                <h3>Account</h3>
                <dl className="kv-list">
                    <dt>Signed up</dt>
                    <dd>{u.signed_up}</dd>
                    <dt>Last active</dt>
                    <dd>{u.last_active}</dd>
                    <dt>Country</dt>
                    <dd>{u.country}</dd>
                    <dt>Onboarding</dt>
                    <dd>{u.onboarding_complete ? 'Complete' : `Step ${u.onboarding_step} of 5`}</dd>
                </dl>
            </section>

            <section className="detail-panel-section">
                <h3>App version & devices</h3>
                {devices.data?.map((d) => (
                    <div
                        key={d.id}
                        style={{
                            padding: '8px 10px',
                            background: 'var(--bg-input)',
                            borderRadius: 6,
                            marginBottom: 6
                        }}
                    >
                        <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                            {d.platform}
                        </div>
                        <div
                            style={{
                                fontSize: 10,
                                color: 'var(--text-muted)',
                                marginTop: 2,
                                display: 'flex',
                                gap: 8
                            }}
                        >
                            <span>v{d.app_version}</span>
                            {d.essentia_version && <span>Essentia {d.essentia_version}</span>}
                            <span>{d.last_seen}</span>
                        </div>
                    </div>
                ))}
            </section>

            <section className="detail-panel-section">
                <h3>Usage</h3>
                <dl className="kv-list">
                    <dt>Tracks</dt>
                    <dd>{u.tracks}</dd>
                    <dt>Storage</dt>
                    <dd>
                        <div className="progress" style={{ minWidth: 140 }}>
                            <div className="progress-track">
                                <div
                                    className="progress-fill"
                                    data-tone={storageTone}
                                    style={{ width: `${storagePct}%` }}
                                />
                            </div>
                            <div className="progress-label">
                                <span className="used">{u.storage_gb.toFixed(1)} GB</span>
                                <span>of {u.storage_limit_gb} GB</span>
                            </div>
                        </div>
                    </dd>
                </dl>
            </section>

            {billing.data && (
                <section className="detail-panel-section">
                    <h3>Billing health</h3>
                    <dl className="kv-list">
                        <dt>Stripe sync</dt>
                        <dd>
                            <Chip
                                tone={
                                    billing.data.stripe_sync === 'ok'
                                        ? 'success'
                                        : billing.data.stripe_sync === 'drifted'
                                          ? 'warning'
                                          : 'danger'
                                }
                            >
                                {billing.data.stripe_sync}
                            </Chip>
                        </dd>
                        <dt>Revenue (lifetime)</dt>
                        <dd>{fmtCurrency(billing.data.revenue_lifetime)}</dd>
                        <dt>Service cost</dt>
                        <dd>
                            {fmtCurrency(
                                Object.values(billing.data.cost_breakdown).reduce(
                                    (s, v) => s + v,
                                    0
                                )
                            )}
                        </dd>
                        <dt>Gross profit</dt>
                        <dd
                            style={{
                                color:
                                    billing.data.gross_profit > 0
                                        ? 'var(--success)'
                                        : 'var(--danger)'
                            }}
                        >
                            {fmtCurrency(billing.data.gross_profit)}
                        </dd>
                        {billing.data.next_charge_date && (
                            <>
                                <dt>Next charge</dt>
                                <dd>
                                    {fmtCurrency(billing.data.next_charge_amount ?? 0)} ·{' '}
                                    {billing.data.next_charge_date}
                                </dd>
                            </>
                        )}
                    </dl>
                    <div
                        style={{
                            marginTop: 10,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 6,
                            fontSize: 10
                        }}
                    >
                        {Object.entries(billing.data.cost_breakdown).map(([k, v]) => (
                            <div
                                key={k}
                                style={{
                                    padding: '6px 8px',
                                    background: 'var(--bg-input)',
                                    borderRadius: 4
                                }}
                            >
                                <div
                                    style={{
                                        color: 'var(--text-muted)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        fontSize: 9,
                                        fontWeight: 600
                                    }}
                                >
                                    {k}
                                </div>
                                <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                                    {fmtCurrency(v)}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {legal.data && (
                <section className="detail-panel-section">
                    <h3>Legal acceptance</h3>
                    <dl className="kv-list">
                        {legal.data.map((l) => (
                            <div key={l.document} style={{ display: 'contents' }}>
                                <dt style={{ textTransform: 'uppercase' }}>{l.document}</dt>
                                <dd>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 4,
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                gap: 6,
                                                alignItems: 'center'
                                            }}
                                        >
                                            <span>{l.version ?? 'Not accepted yet'}</span>
                                            {typeof l.is_current === 'boolean' && (
                                                <Chip tone={l.is_current ? 'success' : 'warning'}>
                                                    {l.is_current ? 'Current' : 'Out of date'}
                                                </Chip>
                                            )}
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                            {l.accepted_at
                                                ? `Accepted ${l.accepted_at}`
                                                : 'No recorded acceptance'}
                                            {l.latest_version
                                                ? ` · latest ${l.latest_version}${
                                                      l.latest_effective_at
                                                          ? ` (${l.latest_effective_at})`
                                                          : ''
                                                  }`
                                                : ''}
                                        </div>
                                    </div>
                                </dd>
                            </div>
                        ))}
                    </dl>
                </section>
            )}

            {audit.data && (
                <section className="detail-panel-section">
                    <h3>User audit</h3>
                    <div className="detail-section-actions">
                        <a
                            className="btn btn-sm btn-ghost"
                            href={`/admin-audit?actor_user_id=${u.id}`}
                        >
                            Actor rows
                        </a>
                        <a
                            className="btn btn-sm btn-ghost"
                            href={`/admin-audit?resource_type=user&resource_id=${u.id}`}
                        >
                            Target rows
                        </a>
                    </div>
                    {auditGroups.length ? (
                        <div className="resource-history">
                            {auditGroups.map((group) => (
                                <section key={group.day}>
                                    <h4>{group.day}</h4>
                                    {group.rows.map((entry) => {
                                        const source = getAuditSource(entry);
                                        return (
                                            <article
                                                className="resource-history-row"
                                                key={entry.id}
                                            >
                                                <header>
                                                    <span className="mono">{entry.ts}</span>
                                                    <Chip
                                                        tone={
                                                            source === 'action' ? 'purple' : 'info'
                                                        }
                                                    >
                                                        {source}
                                                    </Chip>
                                                    <Chip
                                                        tone={
                                                            entry.severity === 'destructive'
                                                                ? 'danger'
                                                                : entry.severity === 'sensitive'
                                                                  ? 'warning'
                                                                  : 'muted'
                                                        }
                                                    >
                                                        {entry.severity}
                                                    </Chip>
                                                </header>
                                                <div className="resource-history-title">
                                                    {entry.action || entry.event}
                                                </div>
                                                <div className="text-muted">
                                                    {entry.actor || 'system'} · {entry.target_type}{' '}
                                                    {getAuditTargetId(entry)}
                                                    {entry.reason || entry.context
                                                        ? ` · ${entry.reason ?? entry.context}`
                                                        : ''}
                                                </div>
                                            </article>
                                        );
                                    })}
                                </section>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state" style={{ padding: 16 }}>
                            <div className="title">No audit rows for this user</div>
                        </div>
                    )}
                </section>
            )}

            <footer className="detail-panel-footer">
                <div className="row">
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-ghost"
                        type="button"
                        disabled={inflight !== null || u.is_admin}
                        title={u.is_admin ? 'Admin users cannot be impersonated' : undefined}
                        onClick={handleImpersonate}
                    >
                        {inflight === 'impersonate' ? 'Impersonating…' : 'Impersonate'}
                    </RoleButton>
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-ghost"
                        type="button"
                        disabled={inflight !== null}
                        onClick={handleMagicLink}
                    >
                        {inflight === 'magic-link' ? 'Sending…' : 'Magic link'}
                    </RoleButton>
                </div>
                <div className="row">
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        type="button"
                        disabled={inflight !== null}
                        onClick={handleForceReauth}
                    >
                        {inflight === 'force-reauth' ? 'Forcing…' : 'Force re-auth'}
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-ghost"
                        type="button"
                        disabled={inflight !== null}
                        onClick={handleForceTos}
                    >
                        {inflight === 'force-tos' ? 'Forcing…' : 'Force ToS re-accept'}
                    </RoleButton>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-danger"
                        type="button"
                        disabled={inflight !== null}
                        onClick={handleToggleSuspend}
                    >
                        {inflight === 'toggle-suspend'
                            ? `${u.status === 'suspended' ? 'Unsuspending' : 'Suspending'}…`
                            : u.status === 'suspended'
                              ? 'Unsuspend'
                              : 'Suspend'}
                    </RoleButton>
                </div>
            </footer>
        </>
    );
}

function normalizeUserAuditEntry(
    entry: UserAuditEntry,
    user: AdminUser
): AdminAuditEntry & Pick<UserAuditEntry, 'context' | 'event'> {
    return {
        ...entry,
        source: entry.source ?? 'event',
        actor: entry.actor ?? user.email,
        actor_id: entry.actor_id ?? user.id,
        actor_role: entry.actor_role ?? 'read_only',
        action: entry.action ?? entry.event ?? 'user.event',
        target_type: entry.target_type ?? 'user',
        target_id: entry.target_id ?? user.id,
        severity: entry.severity ?? 'routine',
        ip: entry.ip ?? ''
    };
}
